import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import axios from 'axios';
import { getPhoneError } from './phoneValidation';
import { COUNTRY_OPTIONS, countryOptionFor } from './countries';
import { companyIdMissingList } from './companyId';
import { deriveWorkPermitIssueDate, issueDateHelper, looksLikeFirstCard } from './labourCard';
import DocSampleBlock from './DocSampleBlock';
import './addTeamMember.css';

const ICP_PHOTO_GUIDELINES_URL = 'https://icp.gov.ae/wp-content/uploads/2021/11/icao_english.pdf';

// Same tiles as Edit. Files picked here are STAGED locally and uploaded right
// after the member is created (uploads need the new user id for presigning).
const DOCUMENT_ROWS = [
    { type: 'personal_photo', label: 'Professional personal photo', starred: true, guidelines: true, note: 'No selfies — professional photos only.' },
    { type: 'passport', label: 'Passport photo', starred: true, note: 'High-quality photo taken flat from directly above (not at an angle) or scanned — all four corners of the passport must be visible.' },
    {
        type: 'last_visa',
        label: 'Residency Visa / Entry Permit / Cancellation',
        starred: true,
        options: [
            { kind: 'residence_visa', text: 'Already on HHC company ID? Upload the residence visa.' },
            { kind: 'cancellation_paper', text: 'Cancelled from a previous company? Upload the cancellation paper.' },
            { kind: 'tourist_entry_permit', text: 'On a tourist visa? Upload the tourist entry permit.' },
        ],
    },
    { type: 'labour_card', label: 'Labour card', samples: [{ kind: 'labour_card', label: 'Labour card' }] },
    { type: 'eid_front', label: 'Emirates ID front' },
    { type: 'eid_back', label: 'Emirates ID back' },
    { type: 'health_insurance_card', label: 'Health insurance card' },
    { type: 'driving_license_front', label: 'Driving license front', driverOnly: true },
    { type: 'driving_license_back', label: 'Driving license back', driverOnly: true },
];

// Rendered inside Team.js's modal shell; the modal header owns the title/close X.
function AddTeamMember({ onClose, onSaved }) {
    const [formData, setFormData] = useState({
        first_name: '',
        middle_name: '',
        last_name: '',
        email: '',
        role: '',
        password: '',
        phone_number: '',
        calling_number: '',
        created_at: '',
        does_have_id: false,
        does_have_id_from_company: false,
        is_trial: false,
        date_of_birth: '',
        place_of_birth: '',
        passport_number: '',
        unified_number: '',
        passport_issue_date: '',
        passport_expiry_date: '',
        passport_issue_country: '',
        passport_issue_place: '',
        eid: '',
        eid_expiry_date: '',
        mohre_person_code: '',
        work_permit_number: '',
        work_permit_issue_date: '',
        work_permit_expiry_date: '',
        health_insurance_expiry: '',
        mohre_salary: '',
        conditional_bonus: '',
    });
    const [roleOptions, setRoleOptions] = useState([]);
    const [fieldErrors, setFieldErrors] = useState({});
    const [dialCodes, setDialCodes] = useState({ phone_number: '971', calling_number: '971' });
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [firstLabourCard, setFirstLabourCard] = useState(false);
    const [stagedDocs, setStagedDocs] = useState({});
    const [docStatus, setDocStatus] = useState({});
    // The member row exists but some staged uploads failed: the form must not
    // be resubmitted (would duplicate the member) — footer flips to Close only.
    const [createdWithUploadErrors, setCreatedWithUploadErrors] = useState(false);

    const userRole = localStorage.getItem('userRole');
    const isManager = userRole === 'manager';

    // Driving-license tiles only apply to drivers — follows the selected role.
    const selectedRoleOption = roleOptions.find((option) => option.value === formData.role);
    const isDriver = String(selectedRoleOption?.label || '').toLowerCase() === 'driver';

    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const response = await axios.get('/api/team/roles');
                const roles = response.data;

                const filteredRoles = roles
                    .filter(role => {
                        if (role.role_name.toLowerCase() === 'client') return false;
                        if ((role.role_name.toLowerCase() === 'manager' || role.role_name.toLowerCase() === 'supervisor') && userRole !== 'manager') return false;
                        return true;
                    })
                    .map(role => ({
                        value: role.id,
                        label: role.role_name.charAt(0).toUpperCase() + role.role_name.slice(1),
                    }));

                setRoleOptions(filteredRoles);
            } catch (error) {
                console.error('Error fetching roles:', error);
                setError('Error fetching roles');
            }
        };

        fetchRoles();
    }, [userRole]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'work_permit_expiry_date') {
            // Auto-fill the issue date from the expiry.
            const isFirstCard = looksLikeFirstCard(value, false);
            setFirstLabourCard(isFirstCard);
            setFormData((prev) => ({
                ...prev,
                work_permit_expiry_date: value,
                work_permit_issue_date: deriveWorkPermitIssueDate(value, isFirstCard),
            }));
            return;
        }
        setFormData({ ...formData, [name]: value });
    };

    const handleFirstCardToggle = (e) => {
        const isFirstCard = e.target.checked;
        setFirstLabourCard(isFirstCard);
        setFormData((prev) => ({
            ...prev,
            work_permit_issue_date: deriveWorkPermitIssueDate(prev.work_permit_expiry_date, isFirstCard),
        }));
    };

    const handleRoleChange = (selectedOption) => {
        setFormData({ ...formData, role: selectedOption.value });
    };

    const handlePhoneChange = (field) => (value, meta) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (meta?.country?.dialCode) {
            setDialCodes((prev) => ({ ...prev, [field]: meta.country.dialCode }));
        }
        setFieldErrors((prev) => ({ ...prev, [field]: null }));
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setFormData((prev) => ({ ...prev, [name]: checked }));
    };

    // Stage a file locally — actual upload happens after the member is created.
    const handleStageDocument = (docType) => (e) => {
        const file = e.target.files?.[0];
        e.target.value = ''; // allow re-picking the same file
        if (!file) return;
        setStagedDocs((prev) => ({ ...prev, [docType]: file }));
        setDocStatus((prev) => ({ ...prev, [docType]: null }));
    };

    const handleUnstageDocument = (docType) => {
        setStagedDocs((prev) => {
            const next = { ...prev };
            delete next[docType];
            return next;
        });
    };

    // Presign → PUT to Spaces → record. Returns null on success, the doc label on failure.
    const uploadStagedDocument = async (userId, docType, file, label) => {
        try {
            setDocStatus((prev) => ({ ...prev, [docType]: { state: 'uploading' } }));
            const contentType = file.type || 'application/octet-stream';
            const presign = await axios.post(`/api/team/${userId}/documents/presign`, {
                doc_type: docType,
                content_type: contentType,
            });
            await axios.put(presign.data.url, file, {
                // Spaces CORS has no Allow-Credentials; the global axios
                // withCredentials=true default would make the browser BLOCK
                // this cross-origin PUT — must be off for presigned uploads.
                withCredentials: false,
                headers: { 'Content-Type': contentType, 'x-amz-acl': 'public-read' },
            });
            await axios.post(`/api/team/${userId}/documents`, {
                doc_type: docType,
                file_key: presign.data.fileKey,
            });
            setDocStatus((prev) => ({ ...prev, [docType]: { state: 'success' } }));
            return null;
        } catch (err) {
            console.error(`Failed to upload ${docType}:`, err);
            setDocStatus((prev) => ({ ...prev, [docType]: { state: 'error', message: 'Upload failed' } }));
            return label;
        }
    };

    const handleAddMember = async (e) => {
        e.preventDefault();

        const phoneErrors = {
            phone_number: getPhoneError('WhatsApp number', formData.phone_number, dialCodes.phone_number),
            calling_number: getPhoneError('Calling number', formData.calling_number, dialCodes.calling_number),
        };
        if (phoneErrors.phone_number || phoneErrors.calling_number) {
            setFieldErrors(phoneErrors);
            setError('Please fix the highlighted contact numbers.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const cleanedFormData = {
                ...formData,
                email: formData.email.trim().toLowerCase(),
                phone_number: formData.phone_number.replace(/[\s-]/g, ''),
                calling_number: formData.calling_number.replace(/[\s-]/g, ''),
            };
            // Manager-only fields never leave a non-manager session (the API
            // ignores them anyway).
            if (!isManager) {
                delete cleanedFormData.mohre_salary;
                delete cleanedFormData.conditional_bonus;
            }

            const response = await axios.post('/api/team/', cleanedFormData);

            // Only 201 will land here – anything else throws
            if (response.status === 201) {
                // Upload any staged documents against the freshly created id.
                const userId = response.data.userId;
                const failedLabels = [];
                for (const row of DOCUMENT_ROWS) {
                    const file = stagedDocs[row.type];
                    if (!file || !userId) continue;
                    const failed = await uploadStagedDocument(userId, row.type, file, row.label);
                    if (failed) failedLabels.push(failed);
                }
                if (failedLabels.length > 0) {
                    // The member EXISTS — resubmitting would duplicate them.
                    setCreatedWithUploadErrors(true);
                    setError(`Member was created, but these documents failed to upload: ${failedLabels.join(', ')}. Open the member via Edit to retry.`);
                    return;
                }
                onSaved();
            } else {
                setError('Error adding team member. Please try again.');
            }
        } catch (error) {
            console.error('Error adding team member:', error);

            if (error.response) {
                // Server responded with a status outside 2xx
                if (error.response.status === 409) {
                    // Use backend message if available
                    setError(
                        error.response.data?.error ||
                        'Email already exists. Please use a different email.'
                    );
                } else if (error.response.data?.error) {
                    setError(error.response.data.error);
                } else {
                    setError('Error adding team member. Please try again.');
                }
            } else {
                // Network / other issue
                setError('Network error. Please check your connection and try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const renderField = (label, name, opts = {}) => (
        <div className="form-group">
            <label>
                {label}
                {opts.starred && <span className="req-star">✱</span>}
            </label>
            <input
                type={opts.type || 'text'}
                name={name}
                value={formData[name]}
                onChange={handleInputChange}
                placeholder={opts.placeholder}
                step={opts.type === 'number' ? '0.01' : undefined}
                min={opts.type === 'number' ? '0' : undefined}
                required={opts.required}
                disabled={opts.disabled}
            />
            {opts.helper && <p className="form-helper">{opts.helper}</p>}
        </div>
    );

    // Live while filling the form: every record still missing for a Company ID
    // employee (their ID processing is already done). Informational only —
    // creating the member is never blocked.
    const cidMissing = companyIdMissingList(formData, (type) => !!stagedDocs[type], isDriver);

    return (
        <form className="add-team-member-form" onSubmit={handleAddMember}>
            <div className="team-passport-banner" role="note">
                <span className="team-passport-banner-icon" aria-hidden="true">📔</span>
                <div>
                    <p className="team-passport-banner-title">Enter all personal details EXACTLY as they appear in the passport.</p>
                    <p className="team-passport-banner-sub">Passport-mismatched details can void company ID processing and may result in financial loss.</p>
                </div>
            </div>

            {cidMissing.length > 0 && (
                <div className="cid-form-alert" role="alert">
                    <p className="cid-form-alert-title">⚠ Company ID staff — {cidMissing.length} record{cidMissing.length === 1 ? '' : 's'} still missing</p>
                    <ul className="cid-form-alert-list">
                        {cidMissing.map((label) => <li key={label}>{label}</li>)}
                    </ul>
                    <p className="cid-form-alert-note">You can still save — add these whenever they're available.</p>
                </div>
            )}

            <section className="team-form-section">
                <h4 className="team-form-section-title">Basic details</h4>
                <div className="team-form-grid team-form-grid-names">
                    {renderField('First Name', 'first_name', { required: true })}
                    {renderField('Middle Name', 'middle_name', { helper: 'Fill in if it appears on the passport.' })}
                    {renderField('Last Name', 'last_name', { required: true })}
                </div>
                <div className="team-form-grid">
                    {renderField('Email', 'email', { type: 'email' })}

                    <div className="form-group">
                        <label>Role</label>
                        <Select
                            classNamePrefix="team-select"
                            options={roleOptions}
                            onChange={handleRoleChange}
                            placeholder="Select Role"
                            required
                        />
                    </div>

                    {renderField('First Working Day', 'created_at', { type: 'date', required: true })}
                    {renderField('Password', 'password', { type: 'password', placeholder: 'Leave blank for auto-generated password' })}

                    <div className="form-group team-form-span-2">
                        <label>Identification status</label>
                        <div className="toggle-list">
                            <label className="toggle-row">
                                <span className="toggle-row-label">Has own Emirates ID</span>
                                <input
                                    type="checkbox"
                                    className="toggle-input"
                                    name="does_have_id"
                                    checked={formData.does_have_id}
                                    onChange={handleCheckboxChange}
                                />
                                <span className="toggle-track" aria-hidden="true">
                                    <span className="toggle-knob" />
                                </span>
                            </label>
                            <label className="toggle-row">
                                <span className="toggle-row-label">Company-provided ID</span>
                                <input
                                    type="checkbox"
                                    className="toggle-input"
                                    name="does_have_id_from_company"
                                    checked={formData.does_have_id_from_company}
                                    onChange={handleCheckboxChange}
                                />
                                <span className="toggle-track" aria-hidden="true">
                                    <span className="toggle-knob" />
                                </span>
                            </label>
                            <label className="toggle-row">
                                <span className="toggle-row-label">Trial staff</span>
                                <input
                                    type="checkbox"
                                    className="toggle-input"
                                    name="is_trial"
                                    checked={formData.is_trial}
                                    onChange={handleCheckboxChange}
                                />
                                <span className="toggle-track" aria-hidden="true">
                                    <span className="toggle-knob" />
                                </span>
                            </label>
                        </div>
                    </div>
                </div>
            </section>

            <section className="team-form-section">
                <h4 className="team-form-section-title">Contact</h4>
                <div className="team-form-grid">
                    <div className="form-group">
                        <label>WhatsApp Number</label>
                        <PhoneInput
                            defaultCountry="ae"
                            value={formData.phone_number}
                            onChange={handlePhoneChange('phone_number')}
                            placeholder="Enter WhatsApp number"
                            className={`client-phone-wrapper${fieldErrors.phone_number ? ' phone-input-error' : ''}`}
                            inputClassName="client-phone-input"
                        />
                        {fieldErrors.phone_number && <p className="field-error">{fieldErrors.phone_number}</p>}
                    </div>
                    <div className="form-group">
                        <label>Calling Number</label>
                        <PhoneInput
                            defaultCountry="ae"
                            value={formData.calling_number}
                            onChange={handlePhoneChange('calling_number')}
                            placeholder="Enter calling number"
                            className={`client-phone-wrapper${fieldErrors.calling_number ? ' phone-input-error' : ''}`}
                            inputClassName="client-phone-input"
                        />
                        {fieldErrors.calling_number && <p className="field-error">{fieldErrors.calling_number}</p>}
                    </div>
                </div>
            </section>

            <section className="team-form-section">
                <h4 className="team-form-section-title">Identity &amp; permits</h4>
                <p className="team-id-legend">
                    <span className="req-star">✱</span>
                    These fields and documents are required for company ID processing.
                </p>
                <div className="team-form-grid">
                    {renderField('Date of Birth', 'date_of_birth', { type: 'date', starred: true })}
                    {renderField('Place of Birth', 'place_of_birth', { starred: true })}
                    {renderField('Passport Number', 'passport_number', { starred: true })}
                    {renderField('Passport Issue Date', 'passport_issue_date', { type: 'date', starred: true })}
                    {renderField('Passport Expiry Date', 'passport_expiry_date', { type: 'date', starred: true })}
                    <div className="form-group">
                        <label>Nationality<span className="req-star">✱</span></label>
                        <Select
                            classNamePrefix="team-select"
                            options={COUNTRY_OPTIONS}
                            value={countryOptionFor(formData.passport_issue_country)}
                            onChange={(opt) => setFormData((prev) => ({ ...prev, passport_issue_country: opt ? opt.value : '' }))}
                            placeholder="Select nationality"
                            isClearable
                        />
                    </div>
                    {renderField('Passport Issue Place', 'passport_issue_place', { starred: true })}
                    {renderField('Unified Number', 'unified_number', { starred: true, helper: (
                        <>
                            Find it on the residence/tourist visa, or in the{' '}
                            <a href="https://smartservices.icp.gov.ae/echannels/web/client/default.html#/fileValidity" target="_blank" rel="noreferrer">
                                ICP system by EID
                            </a>.
                        </>
                    ) })}
                    {renderField('Emirates ID', 'eid')}
                    {renderField('EID Expiry Date', 'eid_expiry_date', { type: 'date' })}
                    {renderField('MOHRE Person Code', 'mohre_person_code', { helper: 'On the labour card as "Person No"' })}
                    {renderField('Work Permit Number', 'work_permit_number', { helper: 'On the Labour Card' })}
                    {renderField('Work Permit Issue Date', 'work_permit_issue_date', { type: 'date', disabled: true, helper: issueDateHelper(firstLabourCard) })}
                    {renderField('Work Permit Expiry Date', 'work_permit_expiry_date', { type: 'date', helper: 'On the Labour Card' })}
                    <div className="form-group">
                        <label className="toggle-row">
                            <span className="toggle-row-label">First labour card (valid 2 months)</span>
                            <input
                                type="checkbox"
                                className="toggle-input"
                                checked={firstLabourCard}
                                onChange={handleFirstCardToggle}
                            />
                            <span className="toggle-track" aria-hidden="true">
                                <span className="toggle-knob" />
                            </span>
                        </label>
                    </div>
                    {renderField('Health Insurance Expiry Date', 'health_insurance_expiry', { type: 'date' })}
                </div>
            </section>

            <section className="team-form-section">
                <h4 className="team-form-section-title">Documents</h4>
                <p className="team-form-section-note">Selected files upload automatically once the member is created.</p>
                <div className="doc-grid">
                    {DOCUMENT_ROWS.filter((row) => !row.driverOnly || isDriver).map(({ type, label, starred, guidelines, note, options, samples }) => {
                        const staged = stagedDocs[type];
                        const status = docStatus[type];
                        return (
                            <div key={type} className="doc-tile">
                                <span className="doc-tile-label">
                                    {label}
                                    {starred && <span className="doc-req-star">✱</span>}
                                </span>
                                {(note || guidelines) && (
                                    <span className="doc-tile-note">
                                        {note}
                                        {guidelines && <> <a className="doc-note-link" href={ICP_PHOTO_GUIDELINES_URL} target="_blank" rel="noreferrer">ICP photo guidelines (PDF)</a></>}
                                    </span>
                                )}
                                <DocSampleBlock options={options} samples={samples} />
                                <div className="doc-tile-status">
                                    {status?.state === 'uploading' ? (
                                        <span className="doc-status-uploading">Uploading…</span>
                                    ) : status?.state === 'error' ? (
                                        <span className="doc-status-error">{status.message}</span>
                                    ) : status?.state === 'success' ? (
                                        <span className="doc-status-uploaded">Uploaded</span>
                                    ) : staged ? (
                                        <span className="doc-status-uploaded">{staged.name}</span>
                                    ) : (
                                        <span className="doc-status-empty">No file selected</span>
                                    )}
                                </div>
                                <div className="doc-tile-actions">
                                    {staged && !loading && !createdWithUploadErrors && (
                                        <button
                                            type="button"
                                            className="doc-remove-button"
                                            onClick={() => handleUnstageDocument(type)}
                                        >
                                            Remove
                                        </button>
                                    )}
                                    <label className={`doc-upload-button${loading || createdWithUploadErrors ? ' doc-upload-disabled' : ''}`}>
                                        {staged ? 'Replace' : 'Choose file'}
                                        <input
                                            type="file"
                                            accept="image/*,application/pdf"
                                            onChange={handleStageDocument(type)}
                                            disabled={loading || createdWithUploadErrors}
                                        />
                                    </label>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {isManager && (
                <section className="team-form-section">
                    <h4 className="team-form-section-title">
                        Compensation
                        <span className="team-section-pill">Manager only</span>
                    </h4>
                    <div className="team-form-grid">
                        {renderField('MOHRE Salary (AED)', 'mohre_salary', { type: 'number' })}
                        {renderField('Conditional Bonus (AED)', 'conditional_bonus', { type: 'number' })}
                    </div>
                </section>
            )}

            <div className="team-form-footer">
                {error && <p className="team-form-footer-error">{error}</p>}
                {createdWithUploadErrors ? (
                    // The member exists — only closing (and refreshing the list)
                    // is safe; a resubmit would create a duplicate.
                    <button type="button" className="team-save-button" onClick={onSaved}>
                        Close
                    </button>
                ) : (
                    <>
                        <button type="button" className="team-cancel-button" onClick={onClose} disabled={loading}>
                            Cancel
                        </button>
                        <button type="submit" className="team-save-button" disabled={loading}>
                            {loading ? 'Adding...' : 'Add Team Member'}
                        </button>
                    </>
                )}
            </div>
        </form>
    );
}

export default AddTeamMember;
